/* eslint-disable react/prop-types */
import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { API_URL, USER_ROLES, USER_TYPES } from '../constant/constant';
import axiosInstance from '../utils/axiosInstance';
import { registerPushToken, unregisterPushToken } from '../services/pushNotificationService';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' | 'email'
  const [emailAddress, setEmailAddress] = useState(null);
  const navigate = useNavigate();
  const [autoOtp, setAutoOtp] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handleLogout = () => {
      setIsAuthenticated(false);
      setPhoneNumber(null);
      setEmailAddress(null);
      // Navigate to login when logout event is received
      navigate('/login', { replace: true });
    };

    window.addEventListener('user-logout', handleLogout);
    return () => {
      window.removeEventListener('user-logout', handleLogout);
    };
  }, [navigate]);

  const handleSuccessfulAuth = async (authData, identifier) => {
    try {
      let data;
      setLoading(true);

      let url;
      if (loginMethod === 'email') {
        url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/email?emailId=${encodeURIComponent(identifier)}`;
      } else {
        url = `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor?mobileNo=%2B91${identifier}`;
      }

      const response = await axiosInstance.get(url, {
        headers: {
          sessionToken: authData?.sessionToken,
          'X-USER-ROLE': USER_ROLES.VENDOR,
        },
      });
      data = await response?.data;

      if (data?.id) {
        if (data?.status !== 'Reject' && data?.status !== 'Pending') {
          setIsAuthenticated(true);

          // Save tokens to localStorage
          localStorage.setItem('contactNo', data?.contactNo);
          localStorage.setItem('token', authData?.sessionToken);
          localStorage.setItem('sessionToken', authData?.sessionToken);
          localStorage.setItem('refreshToken', authData?.refreshToken);
          localStorage.setItem('loginMethod', loginMethod);
          if (loginMethod === 'email') {
            localStorage.setItem('emailAddress', identifier);
          }

          // Register FCM token for push notifications (fire-and-forget, non-blocking)
          registerPushToken(authData?.sessionToken, USER_ROLES.VENDOR, data?.id).catch((err) => {
            console.log('Push notification registration skipped:', err.message);
          });

          // Dispatch event to notify UserContext to refresh
          window.dispatchEvent(
            new CustomEvent('user-login', {
              detail: { phone: data?.contactNo },
            })
          );

          navigate('/store', { replace: true });
          return data;
        } else {
          toast.error('Your registration is under process! Contact administrator @9899784200');
          navigate('/login', { replace: true });
          return null;
        }
      } else {
        setIsModalOpen(true);
        navigate('/login', { replace: true, state: { phoneNumber: identifier } });
        return null;
      }
    } catch (error) {
      console.error('[AuthContext] handleSuccessfulAuth - ERROR', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const initiateLogin = async (phone) => {
    try {
      setLoading(true);

      const { data } = await axiosInstance.post(
        `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/otp/send?phoneNumber=%2B91${phone}&role=${USER_ROLES.VENDOR}`
      );

      const result = data?.failureMsg ? null : 'Otp Sent Successfully';
      return result;
    } catch (error) {
      console.error('[AuthContext] initiateLogin - ERROR', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const initiateLoginByEmail = async (email) => {
    try {
      setLoading(true);

      const { data } = await axiosInstance.post(
        `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/otp/send-email?email=${encodeURIComponent(email)}&role=${USER_ROLES.VENDOR}`
      );

      const result = data?.failureMsg ? null : 'Otp Sent Successfully';
      return result;
    } catch (error) {
      console.error('[AuthContext] initiateLoginByEmail - ERROR', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otp, phone) => {
    setLoading(true);

    try {
      const { data } = await axiosInstance.post(
        `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/otp/verify?phoneNumber=%2B91${phone}&otp=${otp}&role=${USER_ROLES.VENDOR}`
      );
      return data;
    } catch (error) {
      console.error('[AuthContext] verifyOTP - ERROR', error);
      toast.error('Invalid OTP! Please try again');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async (otp, email) => {
    setLoading(true);

    try {
      const { data } = await axiosInstance.post(
        `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/otp/verify-email?email=${encodeURIComponent(email)}&otp=${otp}&role=${USER_ROLES.VENDOR}`
      );
      return data;
    } catch (error) {
      console.error('[AuthContext] verifyEmailOTP - ERROR', error);
      toast.error('Invalid OTP! Please try again');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const phone = localStorage.getItem('contactNo');
      const storedLoginMethod = localStorage.getItem('loginMethod') || 'phone';
      const storedEmail = localStorage.getItem('emailAddress');

      // Determine the identifier used for token storage (phone or email)
      const identifier = storedLoginMethod === 'email' && storedEmail ? storedEmail : phone;

      // Unregister FCM token before logout (fire-and-forget)
      // We need to get the vendor ID before clearing localStorage
      const vendorId = localStorage.getItem('vendorId');
      if (sessionToken && vendorId) {
        unregisterPushToken(sessionToken, USER_ROLES.VENDOR, vendorId).catch((err) => {
          console.log('Push notification unregistration skipped:', err.message);
        });
      }

      // Call logout API with the correct identifier
      if (sessionToken && identifier) {
        await axiosInstance.post(
          `${API_URL.BASE_AUTH}/rest/big-local/api/v1/auth/logout?phoneNumber=${encodeURIComponent(identifier)}&role=${'Vendor'}&accessToken=${sessionToken}`
        );
      }
    } catch (error) {
      console.error('[AuthContext] logout - API error:', error);
    } finally {
      // Always clear state and storage
      setIsAuthenticated(false);
      setPhoneNumber(null);
      setEmailAddress(null);
      setLoginMethod('phone');
      localStorage.clear();

      // Dispatch custom event to notify other contexts
      window.dispatchEvent(new Event('user-logout'));

      navigate('/login', { replace: true });
    }
  };

  const value = {
    isAuthenticated,
    initiateLogin,
    initiateLoginByEmail,
    verifyOTP,
    verifyEmailOTP,
    logout,
    autoOtp,
    setAutoOtp,
    loading,
    phoneNumber,
    setPhoneNumber,
    emailAddress,
    setEmailAddress,
    loginMethod,
    setLoginMethod,
    handleSuccessfulAuth,
    setIsModalOpen,
    isModalOpen,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
